<i18n>
{
    "ru": {
        "reply": "Ответить",
        "open_post": "Перейти",
        "edit": "Редактировать",
        "complain": "Пожаловаться",
        "is_draft": "Черновик",
        "is_draft_moderate": "На модерации",
        "is_draft_rejected": "Снят с публикации",
        "yesterday_at": "Вчера в",
        "today_at": "Сегодня в",
        "at": "в"
    }
}
</i18n>
<template>
    <article
        :id="blockId"
        class="user-tabs-post user-tabs-post"
        :class="rootClasses">
        <header class="user-tabs-post__header">
            <picture class="user-tabs-post-avatar" :class="avatarClasses">
                <img v-lazy-load :data-src="userPhoto" :alt="userName" />
            </picture>

            <div class="user-tabs-post-info" :class="infoClasses">
                <h5 class="user-tabs-post-info__title">
                    <html-link
                        v-if="isPreview && get(post, 'createdBy.htmlUrl')"
                        :url="get(post, 'createdBy.htmlUrl')"
                        class="user-tabs-post-info__link">
                        {{ userName }}
                    </html-link>
                    <span v-else>{{ userName }}</span>
                </h5>
                <p class="user-tabs-post-info__draft" v-if="isDraftModerate">{{ $t('is_draft_moderate') }}</p>
                <p class="user-tabs-post-info__draft" v-else-if="isDraftRejected">{{ $t('is_draft_rejected') }}</p>
                <p class="user-tabs-post-info__draft" v-else-if="isDraft">{{ $t('is_draft') }}</p>
                <p class="user-tabs-post-info__date" v-else-if="showPostDate">{{ datePost }}</p>
            </div>

            <ButtonCebab v-if="showActionsMobile" @click.native="onMoreClick()" />

            <div
                @mouseover="showActions = true"
                @mouseleave="showActions = false"
                v-if="showActionsDesktop"
                class="user-tabs-post__actions">
                <ButtonCebab @click.prevent/>

                <transition name="fade">
                    <Actions
                        is-tooltip
                        v-if="showActions"
                        :modalItems="[
                            {
                                name: $t('complain'),
                                customClass:'popup-actions__link_danger',
                            }
                        ]"/>
                </transition>
            </div>
        </header>

        <div v-if="fullDescription" class="user-tabs-post__description">
            <!-- eslint-disable-next-line -->
            <div v-html="post.text"></div>
        </div>

        <div v-if="isPreview" class="user-tabs-post__description">
            <!-- eslint-disable-next-line -->
            <html-link :url="post.htmlUrl" v-html="post.text" class="user-tabs-post__description-link"></html-link>
        </div>

        <ShowMore
            hideLessButton
            v-else-if="getPostLength"
            class="block-review__content user-tabs-post__description"
            :expanded="expanded"
            :max-rows="maxRows"
            :collaple-from="scrollToBlock"
            :collaple-to="scrollToBlock">
            <!-- eslint-disable-next-line -->
            <div v-html="postDescription"></div>
        </ShowMore>

        <PhotoGallery
            class="user-tabs-post__gallery"
            :data-first-child="!getPostLength"
            :user="userData"
            :data-last-child="!showVideo"
            v-if="showPictures"
            :images="pictures"/>

        <template v-if="showVideo">
            <BaseVideo
                :url="get(videos, '[0].htmlUrl')"
                class="user-tabs-post__video"/>
        </template>

        <TransitionExpand>
            <div class="block-review__replies" v-show="isShowReplies">
                <BlockReview
                    class="block-review__reply"
                    :idPrefix="idPrefix"
                    v-for="reply in replies"
                    :key="reply.id"
                    :can-answer="false"
                    :comment="reply"/>
            </div>
        </TransitionExpand>

        <footer v-if="!withoutFooter" class="user-tabs-post__footer">
            <div v-if="!isDraft"
                class="user-tabs-post__icon-text user-tabs-post__icon-text_likes"
                @click="onLike">
                <svg-icon class="user-tabs-post__icon-like"
                    :name="heartIcon(liked, false)" />
                <span> {{ likesCount || 0 }} </span>
            </div>

            <div
                v-if="!isDraft && noCommentsLink"
                class="user-tabs-post__icon-text user-tabs-post__icon-text_comments">
                <svg-icon name="icon-message-current" />
                <span> {{ comments || 0 }} </span>
            </div>

            <html-link
                v-else-if="!isDraft && post.htmlUrl"
                class="user-tabs-post__icon-text user-tabs-post__icon-text_comments"
                :url="post.htmlUrl + '#comments'">
                <svg-icon name="icon-message-current" />
                <span> {{ comments || 0 }} </span>
            </html-link>

            <div v-if="!noLinks && !isDraft" class="block-article__buttons">
                <button class="user-tabs-post__reply-to" @click.prevent="replyTo">
                    {{ $t('reply') }}
                </button>

                <html-link v-if="post.htmlUrl"
                    class="user-tabs-post__open"
                    :url="post.htmlUrl">

                    {{ $t('open_post') }}
                </html-link>
            </div>

            <button v-else-if="isDraft"
                class="user-tabs-post__edit"
                @click.prevent="editDraftPost">
                {{ $t('edit') }}
            </button>
        </footer>
    </article>
</template>

<script>
import throttle from 'lodash/throttle';
import orderBy from 'lodash/orderBy';
import map from 'lodash/map'
import get from 'lodash/get';
import uniqueId from 'lodash/uniqueId'
import {mapGetters} from 'vuex';
import dayjs from 'dayjs';
import ButtonActionsMixin from '@/mixins/catalog/buttonActions'

import ButtonCebab from '@/components/ui/buttons/ButtonCebab';
import Actions from '@/components/modal/ui/Actions';
import HtmlLink from "~/components/ui/BaseHtmlLink";
import PhotoGallery from '@/components/articles/PhotoGallery'
import Post from "@/models/Post";

export default {
    name: 'UserTabsPost',
    components: {
        HtmlLink,
        ButtonCebab,
        PhotoGallery,
        Actions
    },
    mixins: [ButtonActionsMixin],
    props: {
        idPrefix: {
            type: String,
            default: '',
        },
        post: {
            type: Object,
            default: () => {},
        },
        noLinks: {
            type: Boolean,
            default: false,
        },
        noCommentsLink: {
            type: Boolean,
            default: false,
        },
        expanded: {
            type: Boolean,
            default: false,
        },
        showActionsBtn: {
            type: Boolean,
            default: false,
        },
        fullDescription: {
            type: Boolean,
            default: false
        },
        withoutFooter: {
            type: Boolean,
            default: false
        },
        isPreview: {
            type: Boolean,
            default: false
        }
    },
    globalBus: {
        'fetch-complete'() {
            if(this.post.code) {
                this.userRatings = this.getRatingsByCode(this.post.code,'post');
                this.liked = !!this.userRatings
            }
        }
    },
    data() {
        return {
            isShowReplies: false,
            newCommentsCount: 0,
            replies: [],
            userRatings: null,
            showActions: false,
            liked: false,
            isLikeProcess: false,
            pictures: get(this.post, 'pictures', []),
            videos: get(this.post, 'videos', [])
        };
    },
    computed: {
        ...mapGetters(['isDeviceDesktop', 'getRatingsByCode', 'getUserNoImageLarge']),
        userData() {
            const currentUserData = get(this.$auth, 'user');
            return get(this.post, 'createdBy[0]', currentUserData)
        },
        addEvent() {
            return 'add-new-comment-to-' + this.post.code
        },
        getPostLength() {
            if (!this.post.description) return 0

            const text = this.post.description.replace(/<[^>]+>/g, '')
            return text.length
        },
        maxRows() {
            return 20;
        },
        translations() {
            return {
                lastday_in: this.$t('date.lastday_in'),
                today_in: this.$t('date.today_in'),
                in: this.$t('date.in'),
            };
        },
        datePost() {
            const createdAt = get(this.post, 'createdAt', null);
            if(createdAt) {
                return this.$options.filters.datePost(createdAt, this.translations);
            }

            return null
        },
        likes() {
            return get(this.post, 'likes.total', null);
        },
        likesCount() {
            let likes = this.likes

            if (this.userRatings && likes) likes--
            if (this.liked) likes++

            return likes
        },
        dislikes() {
            return get(this.post, 'ratings.dislikes', 0);
        },
        comments() {
            return get(this.post, 'comments.total', 0) + this.newCommentsCount;
        },
        userName() {
            const currentUserName = get(this.$auth, 'user.fullName', '');
            return get(this.post, 'createdBy[0].fullName', currentUserName);
        },
        userPhoto() {
            const currentUserPhoto = get(this.$auth, 'user.photo.url', this.getUserNoImageLarge);
            if(this.isDraft) return get(this.post, 'createdBy[0].photo.url', currentUserPhoto)

            return get(this.post, 'createdBy[0].photo.url', this.getUserNoImageLarge);
        },
        blockId() {
            const date = new Date(get(this.post, 'createdAt', ''));

            const dateStr = dayjs(date).format('DDMMYYYYhmm');

            return this.idPrefix + '_' + this.userName.replace(/\s+/g, '') + dateStr;
        },
        repliesId() {
            return this.idPrefix + '_' + 'replies' + this.blockId;
        },
        repliesDeclension() {
            return [this.$t('replies_one'), this.$t('replies_few'), this.$t('replies_many')];
        },
        scrollToReplies() {
            return '#' + this.repliesId;
        },
        getReplies() {
            const replies = get(this.postData, 'replies', []);

            return orderBy(
                replies,
                reply => {
                    return new Date(reply.createdAt);
                },
                'asc'
            );
        },
        countReplies() {
            return this.replies.length;
        },
        scrollToBlock() {
            return '#' + this.blockId;
        },
        isDraftRejected() {
            return get(this.post, 'moderation.status', false) === 'rejected'
        },
        isDraftModerate() {
            return get(this.post, 'moderation.status', false) === 'onModeration'
        },
        isDraft() {
            return !get(this.post, 'createdBy', false);
        },
        showActionsMobile() {
            return this.showActionsBtn && !this.isDeviceDesktop
        },
        showActionsDesktop() {
            return this.showActionsBtn && this.isDeviceDesktop
        },
        showPictures() {
            return this.pictures.length > 0 && !this.isPreview
        },
        showVideo() {
            return get(this.videos, '[0].htmlUrl') && !this.isPreview
        },
        showPostDate() {
            return !this.isPreview && !this.post.draft
        },
        avatarClasses() {
            return {
                'user-tabs-post-avatar_preview': this.isPreview
            }
        },
        infoClasses() {
            return {
                'user-tabs-post-info_preview': this.isPreview
            }
        },
        rootClasses() {
            return {
                'user-tabs-post_preview': this.isPreview
            }
        },
        likeEventName() {
            return 'user-like-post-' + this.post.code || this.post.id
        },
        replyToEventName() {
            return 'reply-to-comment-' + this.post.code
        },
        postDescription(){
            const content = this.post.description.split('<span class="media-divider"></span>')

            if(content.length === 2)
                return content[0]

            return this.post.description
        }
    },
    beforeDestroy() {
        if (this.$bus._events[this.replyToEventName])
            this.$bus._events[this.replyToEventName] = []

        this.$bus.$off(this.likeEventName)
        this.$bus.$off(this.addEvent)
    },
    created(){
        if(this.post.description){
            const content = this.post.description.split('<span class="media-divider"></span>')
            if(content.length > 1){
                this.post.description = content[0]
                this.replaceMedia(content[1])
            }
        }
    },
    mounted() {
        this.replies = this.getReplies;

        if (this.post.code && this.$auth.loggedIn) {
            this.userRatings = this.getRatingsByCode(this.post.code,'post');
            this.liked = !!this.userRatings
        }

        // eslint-disable-next-line
        if (process.client) {
            this.$bus.$on(this.addEvent, comment => {
                this.newCommentsCount++
            });

            this.$bus.$on(this.replyToEventName, callback => {
                callback()
            })

            this.$bus.$on(this.likeEventName, async value => {
                const model = new Post({ code: this.post.code });
                if (!model) return

                const notices = {
                    'onLiked': this.$t("alerts.is_liked"),
                    'onUnliked': this.$t("alerts.is_unliked"),
                    'onLikeAlready': this.$t("alerts.liked_already"),
                };

                this.liked = await this.onActionLike(
                    !this.liked,
                    model,
                    notices
                )

                this.isLikeProcess = false;
            })
        }
    },
    methods: {
        get,
        heartIcon(liked) {
            return liked ? 'icon-heart-red' : 'icon-heart-current';
        },
        onLike() {
            if (!this.post.code) return

            if(!this.isLikeProcess){
                this.isLikeProcess = true;
                this.$authEmit(this.likeEventName, !this.liked)
            }
        },
        onVideoClick(src) {
            this.showPopup('video', {
                src,
            });
        },
        triggerShowReplies: throttle(function() {
            this.isShowReplies = !this.isShowReplies;

            if (this.isShowReplies) {
                this.$scrollTo(this.scrollToReplies, 750, {
                    offset: -56,
                });
            } else {
                this.$scrollTo(this.scrollToBlock, 750, {
                    offset: -56,
                });
            }
        }),
        replyTo() {
            this.$authEmit(this.replyToEventName, () => {
                this.showPopup('popup-comment', {
                    page: 'post',
                    code: this.post.code,
                    addEvent: this.addEvent
                });
            });
        },
        editDraftPost() {
            this.showPopup('content-editor', {
                type: 'posts',
                draftId: this.post.id,
                updateEvent: 'posts-draft-updated'
            })
        },
        onMoreClick() {
            const modalItems = [
                {
                    name: 'Пожаловаться',
                    customClass: 'popup-actions__link_danger',
                },
            ];
            this.showPopup('actions', {
                modalItems,
            });
        },
        replaceMedia(content){
            const patterns = {
                image: /<img [^>]*src="[^"]*"[^>]*>/gm,
                iframe: /<iframe [^>]*src="[^"]*"[^>]*>/gm,
                alt: /.*alt="([^"]*)".*/,
                src: /.*src="([^"]*)".*/,
                dsrc: /.*data-src="([^"]*)".*/,
                width: /.*width="([^"]*)".*/,
                height: /.*height="([^"]*)".*/,
                scale: /.*data-scaling="([^"]*)".*/,
            }

            const rawImages = content.match(patterns.image)

            if (rawImages && rawImages.length) {
                this.pictures = map(rawImages, x => {
                    const src = get(x.match(patterns.src), '[1]') || get(x.match(patterns.dsrc))
                    const alt = escape(get(x.match(patterns.alt), '[1]'))
                    const width = get(x.match(patterns.width), '[1]')
                    const height = get(x.match(patterns.height), '[1]')
                    const scale = get(x.match(patterns.scale), '[1]')

                    const image = {}

                    if (src) {
                        image.src = src
                        image.url = src
                        image.width = width
                        image.height = height
                        image.scale = scale
                    }

                    image.alt = image.title = alt || ''

                    image.index = uniqueId('gallery_editor_')

                    if (image.src) return image
                })
            }

            const rawVideo = content.match(patterns.iframe)

            if (rawVideo && rawVideo.length) {
                const videoObj = map(rawVideo, x => {
                    const src = get(x.match(patterns.src), '[1]') || get(x.match(patterns.dsrc), '[1]')

                    const video = {}

                    if(src){
                        video.src = src
                        video.htmlUrl = src
                    }

                    video.index = uniqueId('gallery_editor_')

                    if (video.src) return video
                })

                this.videos = [videoObj[0]]
            }
        }
    },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active до версии 2.1.8 */ {
    opacity: 0;
}
</style>

<style lang="less">
.user-tabs-post {
    border-bottom: rem(1) solid @color-gray-white;
    border-top: none;

    padding: rem(21) rem(20) rem(23);

    &:last-child {
        @{mobile} & {
            border-bottom: none;
        }
    }

    @{desktop} & {
        padding: 0 0 rem(25);
    }

    &__gallery {
        @{mobile} &,
        @{desktop} & {
            margin-top: rem(16);
            margin-bottom: rem(16);

            &[data-first-child="true"] {
                margin-top: 0;
            }

            &[data-last-child="true"] {
                margin-bottom: 0;
            }
        }

        @{desktop} & {
            --row-big: rem(276);
            --row-regular: rem(173);
            --row-middle: rem(199);
            --row-small: rem(134);
            --row-single: rem(410);
        }
    }

    .photo-gallery__image {
        border-radius: rem(3);

        &:only-child{
            height: rem(410);
            background-color: @color-bg-gray;
            width: rem(616);
            object-fit: cover;
            border-radius: rem(3);

            &[data-scaling="fitted"]{
                object-fit: contain;
            }

            @{mobile} & {
                height: 100%;
                width: 100%;
            }
        }
    }

    img {
        max-width: 100%;
    }

    &__actions {
        position: relative;
        z-index: 100;
    }

    &__header {
        display: flex;
        flex-flow: row nowrap;
        align-items: flex-start;

        &+.user-tabs-post__video{
            margin-top: 0;
        }
    }

    &__picture,
    &__video {
        display: flex;
        align-items: center;
        justify-content: center;

        border-radius: rem(3);

        margin-top: rem(16);

        height: 100%;
        width: 100%;

        position: relative;

        overflow: hidden;

        img {
            object-fit: cover;
            cursor: pointer;

            height: calc(100vw * 223 / 375);
            width: 100%;
            max-height: 100%;
        }

        @{desktop} & {
            height: rem(410);
            --frame-size: 100%;
        }

        @{mobile} & {
            --frame-size: rem(223);
        }
    }

    &__picture_draft img {

        @{desktop} & {
            cursor: default;
        }
    }

    &__video {
        --frame-size: 100%;

        margin-top: rem(16);

        position: relative;

        &:after {
            content: '';
            display: block;
            cursor: pointer;

            background-color: rgba(0, 0, 0, 0.5);
            background-image: url(/img/play.svg);
            background-position: center;
            background-repeat: no-repeat;
            background-size: rem(40) rem(40);

            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
        }
    }

    &__reply-to,
    &__open,
    &__edit {
        background: none;
        border: none;
        color: @color-gray-light;

        padding: 0;

        .animate(opacity);

        .hover({
            color: @color-gray;
        });
    }

    &__edit {
        margin-left: auto;
    }

    &__open {
        margin-left: rem(16);
    }

    &__footer {
        display: flex;
        justify-content: space-between;

        padding-top: rem(16);
    }

    &__icon-text {
        color: @color-gray-light;
        font-size: rem(13);
        font-weight: 500;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        transition: color .15s ease;

        svg {
            height: rem(20);
            width: rem(20);
        }

        svg ~ span {
            margin-left: rem(2);
        }

        span ~ svg {
            margin-left: rem(2);
        }

        span {
            position: relative;
            top: rem(1);
        }

        &_likes {
            margin-right: rem(22);
        }

        &_comments {
            margin-right: auto;
            .hover({
                color: @color-gray;
            });
        }

        .hover({
            cursor: pointer;
            color: @color-gray;
        });
    }

    &__description {

        @{desktop} & {
            line-height: 1.55;
        }

        a{
            &:hover{
                text-decoration: underline !important; 
            }
        }

        p:last-child {
            margin-bottom: rem(5);
        }

        &-link {
            color: @color-black;
            font-size: rem(15);

            max-height: rem(46);

            .text-ellipsis(2);

            @{desktop} & {
                max-height: rem(70);

                .text-ellipsis(3);

                .hover({
                    color: @color-violet;
                })
            }
        }

        // figure,
        // .photo-gallery{
        //     display: none;
        // }

        // iframe{
        //     visibility: hidden;
        //     width: 100%;
        //     display: block;
        //     height: rem(410);

        //     @{mobile} & {
        //         height: rem(223);
        //     }
        // }
    }

    &_preview {
        border-bottom: 1px solid @color-gray-white;
        border-top: none;

        padding-top: 0;
        padding-bottom: rem(16);

        + .user-tabs-post_preview {
            padding-top: rem(13);
        }

        .user-tabs-post-info__title {
            font-size: rem(14);
            font-weight: 400;

            padding-top: rem(3);
        }

        .user-tabs-post__header {
            margin-bottom: rem(8);
        }

        @{desktop} & {
            padding-bottom: rem(13);

            + .user-tabs-post_preview {
                padding-top: rem(13);
            }

            .user-tabs-post-info__title {
                font-size: rem(14);
                font-weight: 400;

                padding-top: rem(3);
            }
        }
    }
}

.user-tabs-post-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    height: rem(41);
    width: rem(41);
    overflow: hidden;
    backface-visibility: hidden;
    transform: translate3d(0, 0, 0);

    img {
        object-fit: cover;
        height: 100%;
        width: 100%;
        max-width: 100%;
        max-height: 100%;
    }

    &_preview {
        height: rem(24);
        width: rem(24);
    }

}

.user-tabs-post-info {
    margin-bottom: rem(16);
    padding-left: rem(12);

    width: calc(100% - rem(40));

    &__title {
        font-size: rem(15);
        line-height: 1.6;

        margin-bottom: rem(3);
    }

    &__draft,
    &__date {
        color: @color-gray;
        font-size: rem(13);

        margin: 0;
    }

    &__draft {
        color: @color-red;
    }

    &__link {
        color: @color-gray;

        .hover({
            color: @color-violet;
        })
    }

    &_preview {
        margin-bottom: 0;
        padding-left: rem(12);

        @{desktop} & {
            margin-bottom: 0;
            padding-left: rem(12);
        }
    }
}
</style>
